import React, { useState, useEffect } from 'react';
import { getMembers, bulkApproveMembers, deleteMember, restoreMember } from '../services/api';
import MemberDetailModal from '../components/MemberDetailModal';

function MembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState(null);
  const [approvalFilter, setApprovalFilter] = useState(null); // null | 'approved' | 'pending' | 'deleted'
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllPending, setSelectAllPending] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [rowActionId, setRowActionId] = useState(null);
  const userRole = localStorage.getItem('role');
  const itemsPerPage = 20;

  useEffect(() => { loadMembers(); }, []);

  const loadMembers = async () => {
    setLoading(true);
    try {
      const res = await getMembers();
      setMembers(res.data);
    } catch (e) {
      console.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const filtered = members.filter(m => {
    if (approvalFilter === 'deleted') {
      if (!m.deleted) return false;
    } else {
      if (m.deleted) return false;
      if (approvalFilter === 'approved' && !m.approved) return false;
      if (approvalFilter === 'pending' && m.approved) return false;
    }
    if (sourceFilter && (m.utmSource || 'Direct') !== sourceFilter) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return m.name?.toLowerCase().includes(q) || m.civilIdNo?.includes(q) ||
           m.mobileNo?.includes(q) || m.email?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const sorted = [...filtered].sort((a, b) => new Date(b.submittedAt || 0) - new Date(a.submittedAt || 0));
  const current = sorted.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const activeMembers = members.filter(m => !m.deleted);
  const approvedCount = activeMembers.filter(m => m.approved).length;
  const pendingCount = activeMembers.length - approvedCount;
  const deletedCount = members.filter(m => m.deleted).length;

  const sourceCounts = activeMembers.reduce((acc, m) => {
    const src = m.utmSource || 'Direct';
    acc[src] = (acc[src] || 0) + 1;
    return acc;
  }, {});
  const sourceColors = ['#2d5f3f','#1565c0','#d32f2f','#ff9800','#7b1fa2','#00838f','#4e342e','#546e7a'];

  const allPending = filtered.filter(m => !m.approved);

  const toggleSelect = (id) => {
    setSelectAllPending(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingOnPage = current.filter(m => !m.approved);
    const allSelected = pendingOnPage.every(m => selectedIds.has(m.id));
    setSelectAllPending(false);
    setSelectedIds(prev => {
      const next = new Set(prev);
      pendingOnPage.forEach(m => allSelected ? next.delete(m.id) : next.add(m.id));
      return next;
    });
  };

  const selectAllPendingMembers = () => {
    setSelectAllPending(true);
    setSelectedIds(new Set(allPending.map(m => m.id)));
  };

  const clearSelection = () => {
    setSelectAllPending(false);
    setSelectedIds(new Set());
  };

  const handleRowDelete = async (m) => {
    if (!window.confirm(`Mark "${m.name}" as deleted? They will be hidden and cannot verify until restored.`)) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setRowActionId(m.id);
    try {
      await deleteMember(m.id, user.username || localStorage.getItem('username') || '');
      await loadMembers();
    } catch (e) {
      alert('Failed to mark as deleted');
    } finally {
      setRowActionId(null);
    }
  };

  const handleRowRestore = async (m) => {
    if (!window.confirm(`Restore "${m.name}"?`)) return;
    setRowActionId(m.id);
    try {
      await restoreMember(m.id);
      await loadMembers();
    } catch (e) {
      alert('Failed to restore');
    } finally {
      setRowActionId(null);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setBulkLoading(true);
    try {
      await bulkApproveMembers([...selectedIds], user.username || 'admin');
      setSelectedIds(new Set());
      setSelectAllPending(false);
      await loadMembers();
    } catch (e) {
      alert('Bulk approve failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const pendingOnPage = current.filter(m => !m.approved);
  const allPageSelected = pendingOnPage.length > 0 && pendingOnPage.every(m => selectedIds.has(m.id));

  return (
    <div className="card">
      {!loading && members.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:'12px',marginBottom:'20px'}}>
          {Object.entries(sourceCounts).sort((a,b) => b[1]-a[1]).map(([src, count], i) => (
            <div key={src} onClick={() => { setSourceFilter(sourceFilter === src ? null : src); setCurrentPage(1); }} style={{padding:'12px 18px',borderRadius:'8px',backgroundColor:sourceColors[i % sourceColors.length],color:'#fff',minWidth:'120px',textAlign:'center',cursor:'pointer',opacity:sourceFilter && sourceFilter !== src ? 0.4 : 1,outline:sourceFilter === src ? '3px solid #000' : 'none',transition:'opacity 0.2s'}}>
              <div style={{fontSize:'24px',fontWeight:'bold'}}>{count}</div>
              <div style={{fontSize:'12px',opacity:0.9}}>{src}</div>
            </div>
          ))}
          <div onClick={() => { setSourceFilter(null); setCurrentPage(1); }} style={{padding:'12px 18px',borderRadius:'8px',backgroundColor:'#333',color:'#fff',minWidth:'120px',textAlign:'center',cursor:'pointer',opacity:sourceFilter ? 0.4 : 1,outline:!sourceFilter ? '3px solid #000' : 'none',transition:'opacity 0.2s'}}>
            <div style={{fontSize:'24px',fontWeight:'bold'}}>{activeMembers.length}</div>
            <div style={{fontSize:'12px',opacity:0.9}}>Total</div>
          </div>
        </div>
      )}

      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
        <h2>Membership Registrations</h2>
        <div style={{fontSize: '18px', fontWeight: 'bold', color: '#2d5f3f'}}>Total: {filtered.length}</div>
      </div>

      <div style={{display:'flex',gap:'10px',marginBottom:'20px',flexWrap:'wrap',alignItems:'center'}}>
        <input type="text" placeholder="Search by name, civil ID, mobile, email..." value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
          style={{flex:1,minWidth:'200px',padding:'12px',fontSize:'16px',border:'1px solid #ddd',borderRadius:'5px',outline:'none'}} />
        <div style={{display:'flex',border:'1px solid #ddd',borderRadius:'5px',overflow:'hidden'}}>
          {[
            {key:null,label:'All',count:activeMembers.length},
            {key:'approved',label:'Approved',count:approvedCount},
            {key:'pending',label:'Pending',count:pendingCount},
            ...(userRole === 'MASTER' ? [{key:'deleted',label:'Deleted',count:deletedCount}] : [])
          ].map(f => (
            <button key={f.label} onClick={() => { setApprovalFilter(f.key); setCurrentPage(1); setSelectedIds(new Set()); setSelectAllPending(false); }}
              style={{padding:'10px 14px',border:'none',cursor:'pointer',fontSize:'13px',fontWeight:approvalFilter===f.key?'bold':'normal',
                backgroundColor:approvalFilter===f.key?'#2d5f3f':'#fff',color:approvalFilter===f.key?'#fff':'#333',transition:'all 0.2s'}}>
              {f.label} ({f.count})
            </button>
          ))}
        </div>
        {selectedIds.size > 0 && (
          <button className="btn" onClick={handleBulkApprove} disabled={bulkLoading}
            style={{backgroundColor:'#2d5f3f',color:'#fff',padding:'12px 20px',border:'none',borderRadius:'5px',cursor:'pointer',fontWeight:'bold',opacity:bulkLoading?0.6:1}}>
            {bulkLoading ? 'Approving...' : `Approve Selected (${selectedIds.size})`}
          </button>
        )}
      </div>

      {loading ? (
        <div style={{textAlign: 'center', padding: '60px 0'}}><div className="spinner"></div></div>
      ) : (
        <>
          <table className="table" style={{tableLayout:'fixed',width:'100%'}}>
            {allPageSelected && allPending.length > pendingOnPage.length && (
              <caption style={{captionSide:'top',padding:'10px 16px',backgroundColor:'#fff8e1',borderRadius:'5px',marginBottom:'4px',fontSize:'13px',textAlign:'center'}}>
                {selectAllPending ? (
                  <span>All <strong>{allPending.length}</strong> pending members selected. <button onClick={clearSelection} style={{background:'none',border:'none',color:'#1565c0',cursor:'pointer',fontWeight:'bold',textDecoration:'underline',fontSize:'13px'}}>Clear selection</button></span>
                ) : (
                  <span>All <strong>{pendingOnPage.length}</strong> pending on this page selected. <button onClick={selectAllPendingMembers} style={{background:'none',border:'none',color:'#1565c0',cursor:'pointer',fontWeight:'bold',textDecoration:'underline',fontSize:'13px'}}>Select all {allPending.length} pending members</button></span>
                )}
              </caption>
            )}
            <thead>
              <tr>
                <th style={{width:'36px'}}>
                  <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll}
                    title="Select all pending on this page" aria-label="Select all pending members on this page" />
                </th>
                <th>Name</th>
                <th style={{width:'70px'}}>Civil ID</th>
                <th style={{width:'95px'}}>Mobile</th>
                <th style={{width:'60px'}}>Ref</th>
                <th style={{width:'75px'}}>Status</th>
                <th style={{width:'90px'}}>Registered</th>
                <th style={{width: userRole === 'MASTER' ? '80px' : '36px'}}></th>
              </tr>
            </thead>
            <tbody>
              {current.map(m => (
                <tr key={m.id} style={{backgroundColor: m.deleted ? '#fbe9e7' : (selectedIds.has(m.id) ? '#e8f5e9' : undefined), opacity: m.deleted ? 0.75 : 1}}>
                  <td>
                    {!m.approved && !m.deleted && (
                      <input type="checkbox" checked={selectedIds.has(m.id)} onChange={() => toggleSelect(m.id)}
                        aria-label={`Select ${m.name}`} />
                    )}
                  </td>
                  <td style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}><span style={{color:'#555',verticalAlign:'middle',marginRight:'4px',display:'inline-flex'}} title={m.gender}>{m.gender?.toLowerCase() === 'male' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                  ) : m.gender?.toLowerCase() === 'female' ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-3-3.87"/><path d="M4 21v-2a4 4 0 0 1 3-3.87"/><circle cx="12" cy="7" r="4"/><line x1="12" y1="11" x2="12" y2="17"/><line x1="9" y1="14" x2="15" y2="14"/></svg>
                  ) : null}</span><strong>{m.name}</strong></td>
                  <td style={{fontFamily:'monospace',fontSize:'13px'}}>{m.civilIdNo ? '*' + m.civilIdNo.slice(-5) : '-'}</td>
                  <td style={{fontSize:'13px'}}>{m.mobileNo ? <a href={`tel:${m.mobileNo}`} style={{color:'inherit',textDecoration:'none'}}>{m.mobileNo}</a> : '-'}</td>
                  <td style={{fontSize:'12px'}}>{m.utmSource || '-'}</td>
                  <td>
                    {m.deleted ? (
                      <span style={{color:'#c62828',fontWeight:'bold',fontSize:'12px'}}>🗑 Deleted</span>
                    ) : m.approved ? (
                      <span style={{color:'#2e7d32',fontWeight:'bold',fontSize:'12px'}}>✓ Approved</span>
                    ) : (
                      <span style={{color:'#e65100',fontWeight:'bold',fontSize:'12px'}}>⏳ Pending</span>
                    )}
                  </td>
                  <td style={{fontSize:'12px',color:'#555',whiteSpace:'nowrap'}}>{m.submittedAt ? new Date(m.submittedAt).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit'}).replace(/\//g,'/') + ' ' + new Date(m.submittedAt).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true}).toLowerCase() : '-'}</td>
                  <td>
                    <div style={{display:'inline-flex',gap:'4px',alignItems:'center'}}>
                      <button onClick={() => setSelectedMemberId(m.id)} title="View details" aria-label={`View ${m.name}`}
                        style={{background:'#2d5f3f',border:'none',cursor:'pointer',padding:'5px 8px',lineHeight:1,color:'#fff',borderRadius:'4px',display:'inline-flex',alignItems:'center'}}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                      </button>
                      {userRole === 'MASTER' && !m.deleted && (
                        <button onClick={() => handleRowDelete(m)} disabled={rowActionId === m.id} title="Mark as deleted" aria-label={`Delete ${m.name}`}
                          style={{background:'#c62828',border:'none',cursor:'pointer',padding:'5px 8px',lineHeight:1,color:'#fff',borderRadius:'4px',display:'inline-flex',alignItems:'center',opacity:rowActionId===m.id?0.6:1}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                      )}
                      {userRole === 'MASTER' && m.deleted && (
                        <button onClick={() => handleRowRestore(m)} disabled={rowActionId === m.id} title="Restore member" aria-label={`Restore ${m.name}`}
                          style={{background:'#1565c0',border:'none',cursor:'pointer',padding:'5px 8px',lineHeight:1,color:'#fff',borderRadius:'4px',display:'inline-flex',alignItems:'center',opacity:rowActionId===m.id?0.6:1}}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><polyline points="3 4 3 10 9 10"/></svg>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginTop: '20px'}}>
              <button className="btn btn-primary" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} style={{opacity: currentPage === 1 ? 0.5 : 1}}>←</button>
              {(() => {
                const pages = [];
                const add = (p) => { if (!pages.includes(p)) pages.push(p); };
                add(1);
                for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) add(i);
                add(totalPages);
                const items = [];
                for (let i = 0; i < pages.length; i++) {
                  if (i > 0 && pages[i] - pages[i-1] > 1) items.push(<span key={'e'+i} style={{padding:'0 4px',color:'#999'}}>…</span>);
                  items.push(
                    <button key={pages[i]} onClick={() => setCurrentPage(pages[i])}
                      style={{padding:'8px 12px',border:'1px solid #2d5f3f',backgroundColor:currentPage===pages[i]?'#2d5f3f':'white',color:currentPage===pages[i]?'white':'#2d5f3f',borderRadius:'5px',cursor:'pointer',fontWeight:currentPage===pages[i]?'bold':'normal',minWidth:'38px'}}>
                      {pages[i]}
                    </button>
                  );
                }
                return items;
              })()}
              <button className="btn btn-primary" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} style={{opacity: currentPage === totalPages ? 0.5 : 1}}>→</button>
              <span style={{marginLeft:'12px',fontSize:'13px',color:'#666'}}>Page {currentPage} of {totalPages}</span>
            </div>
          )}
        </>
      )}

      {selectedMemberId && (
        <MemberDetailModal memberId={selectedMemberId} onClose={() => { setSelectedMemberId(null); loadMembers(); }} />
      )}
    </div>
  );
}

export default MembersPage;
